import { Box, Button, Paper, Typography } from "@mui/material";
import { useParams } from "react-router-dom";
import { TransitionPage } from "../../App";
import { useThrottledHandler } from "../../State/Hooks/useThrottledHandler";
import { QueryProvider } from "../../Queries/QueryProvider";
import { useChangeSubscriptionStatus, useSubscriptionStatus } from "../../Queries/SubscriptionQueries";

const isGuid = (value: string) => {
  return value.match(/^[0-9a-fA-F]{8}-([0-9a-fA-F]{4}-){3}[0-9a-fA-F]{12}$/);
};

interface IRouteParams extends Record<string, string | undefined> {
  messageId: string;
}

export default function ManageSubscription() {
  return (
    <QueryProvider>
      <SubscriptionStatus />
    </QueryProvider>
  );
}

function SubscriptionStatus() {
  const { messageId } = useParams<IRouteParams>();
  const { data: emailSubscriptionStatus, isLoading, error } = useSubscriptionStatus(messageId);

  if (!messageId || !isGuid(messageId)) {
    return (
      <PageMessageWrapper>
        <TransitionPage
          message="Missing or invalid message ID"
          messageSx={{ fontSize: "2rem", color: "error.main" }}
          caption="Please contact your administrator if the problem persists."
          boxSx={{ position: "static" }}
        />
      </PageMessageWrapper>
    );
  }

  if (error) {
    return (
      <PageMessageWrapper>
        <TransitionPage
          message="Error Getting Email Subscription Status"
          messageSx={{ color: "error.main" }}
          caption={error.message}
          boxSx={{ position: "static" }}
        />
      </PageMessageWrapper>
    );
  }

  if (!emailSubscriptionStatus || isLoading) {
    return (
      <PageMessageWrapper>
        <TransitionPage message="Loading..." boxSx={{ position: "static" }} />
      </PageMessageWrapper>
    );
  }

  if (!emailSubscriptionStatus.candidateId) {
    return (
      <PageMessageWrapper>
        <TransitionPage
          message="Could not find your email subscription status"
          messageSx={{ fontSize: "2rem", color: "error.main", lineHeight: 1, mb: 2, textAlign: "center" }}
          caption="Please contact your administrator if the problem persists."
          boxSx={{ position: "static" }}
        />
      </PageMessageWrapper>
    );
  }

  const isSubscribed = !emailSubscriptionStatus.hasUnsubscribed;
  return (
    <PageMessageWrapper>
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", flexDirection: "column" }}>
        <Box
          sx={{ display: "flex", justifyContent: "center", alignItems: "center", flexDirection: "column" }}
        >
          <Typography
            sx={{
              color: isSubscribed ? "primary.main" : "warning.main",
              fontWeight: 700,
              fontSize: "1.75rem",
              fontFamily: "h1.fontFamily",
            }}
          >
            {emailSubscriptionStatus.emailAddress}
          </Typography>
          <Typography sx={{ textAlign: "center" }}>
            {isSubscribed ? "You're subscribed to emails from" : "You're unsubscribed from emails from"}
          </Typography>
          <Typography sx={{ textAlign: "center", fontWeight: "bold" }}>
            {emailSubscriptionStatus.clientName}
          </Typography>
        </Box>
        <Box
          sx={{ display: "flex", justifyContent: "center", alignItems: "center", flexDirection: "column" }}
        >
          {isSubscribed ? (
            <UnsubscribeButton candidateId={emailSubscriptionStatus.candidateId} messageId={messageId} />
          ) : (
            <ResubscribeButton candidateId={emailSubscriptionStatus.candidateId} messageId={messageId} />
          )}
        </Box>
      </Box>
    </PageMessageWrapper>
  );
}

function ResubscribeButton({ candidateId, messageId }: { candidateId: string; messageId: string }) {
  const { mutate: updateSubscriptionStatus, error, isLoading } = useChangeSubscriptionStatus();
  const handleResubscribe = useThrottledHandler(() =>
    updateSubscriptionStatus({ candidateId, messageId, subscribed: true }),
  );

  if (error) {
    console.error(error);
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          flexDirection: "column",
          mt: 4,
        }}
      >
        <Typography sx={{ textAlign: "center" }}>
          There was an error resubscribing to emails. Please contact your administrator if the problem
          persists.
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{ display: "flex", justifyContent: "center", alignItems: "center", flexDirection: "column", mt: 4 }}
    >
      <Typography sx={{ textAlign: "center" }}>
        You won't receive any more emails from this client. If this was a mistake, click the button below to
        resubscribe.
      </Typography>
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", flexDirection: "column" }}>
        <Button
          id="action-button"
          data-test="resubscribe-button"
          aria-label="Resubscribe to Emails"
          variant="outlined"
          onClick={handleResubscribe}
          disabled={isLoading}
        >
          Resubscribe
        </Button>
      </Box>
    </Box>
  );
}

function UnsubscribeButton({ candidateId, messageId }: { candidateId: string; messageId: string }) {
  const { mutate: updateSubscriptionStatus, error, isLoading } = useChangeSubscriptionStatus();
  const handleUnsubscribe = useThrottledHandler(() =>
    updateSubscriptionStatus({ candidateId, messageId, subscribed: false }),
  );
  if (error) {
    console.error(error);
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          flexDirection: "column",
          mt: 4,
        }}
      >
        <Typography sx={{ textAlign: "center" }}>
          There was an error unsubscribing you from emails from this client. Please contact your administrator
          if the problem persists.
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        flexDirection: "column",
        mt: 4,
      }}
    >
      <Button
        id="action-button"
        data-test="unsubscribe-button"
        aria-label="Unsubscribe to Emails"
        variant="outlined"
        onClick={handleUnsubscribe}
        color="info"
        disabled={isLoading}
      >
        Unsubscribe
      </Button>
    </Box>
  );
}

function PageMessageWrapper({ children }: { children: React.ReactNode }) {
  return (
    <Box
      sx={{
        position: "absolute",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      <Paper sx={{ width: "100%", maxWidth: "500px", p: 4 }}>{children}</Paper>
    </Box>
  );
}
